import { useState } from "react";

import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { LANG_TOGGLE_LIST } from "@/constants/Language";
import NavigationButtons from "@/view/NavigationButtons";
import ProgressBar from "@/view/ProgressBar";
import QuizElaStandardsSection from "@/view/QuizElaStandardsSection";
import QuizToggleAnswersButton from "@/view/QuizToggleAnswersButton";
import useReadingSkillsContext from "@/view/utils/useReadingSkillsContext/useReadingSkillsContext";
import QuizQuestion from "@/view/QuizQuestion/QuizQuestion.tsx";
import {
  questionCountContainerCss,
  rootCss,
} from "./QuizPreviewContainer.style";

const IS_READING_SKILL_SELECTED = "isReadingSkillSelected";

const propTypes = {
  isPresentModeActive: PropTypes.bool,
  quizData: PropTypes.object,
  quizQuestionMetadata: PropTypes.object,
  language: PropTypes.oneOf(LANG_TOGGLE_LIST),
};

const getSelectedQuestions = (readingSkillState, quizData) => {
  let selectedQuestionIds = [];
  for (const value of Object.values(readingSkillState)) {
    const questionIds = Object.keys(value).filter(
      (data) => data !== IS_READING_SKILL_SELECTED,
    );
    const selectedQuestions = questionIds.filter(
      (id) => value[id].isQuizQuestionSelected === true,
    );

    selectedQuestionIds = selectedQuestionIds.concat(selectedQuestions);
  }

  const quizDataQuestions = quizData?.questions ?? [];
  const filteredQuizData = quizDataQuestions.filter((question) =>
    selectedQuestionIds.includes(question?.id),
  );

  if (selectedQuestionIds.length === quizDataQuestions.length) {
    return null;
  }

  return filteredQuizData;
};

const QuizPreviewContainer = ({
  isPresentModeActive,
  quizData,
  quizQuestionMetadata,
  language,
}) => {
  const [showAnswerInPresentMode, setShowAnswerInPresentMode] = useState(false);
  const router = useRouter();
  const {
    pathname,
    query,
    query: { levelId, quizId, question: questionPosition },
  } = router;

  const { readingSkillState } = useReadingSkillsContext();
  const selectedQuestions = getSelectedQuestions(readingSkillState, quizData);
  const currentQuestions = selectedQuestions || quizData?.questions;

  const onClickNavigationButtons = (navigateTo) => {
    setShowAnswerInPresentMode(false);
    const href = {
      pathname,
      query: {
        ...query,
        question:
          navigateTo === "next"
            ? parseInt(questionPosition) + 1
            : parseInt(questionPosition) - 1,
      },
    };

    switch (navigateTo) {
      case "next":
      case "back":
        router.replace(href, null, { shallow: true });
        break;
    }
  };

  const questionIndex = questionPosition - 1;
  const questionCount = currentQuestions?.length;
  const currentQuestion = currentQuestions?.[questionIndex];
  const questionMetadata = quizQuestionMetadata?.[currentQuestion?.id];
  const questionStandard =
    questionMetadata?.data?.find((item) => item.content_type === "STANDARD") ||
    null;

  return (
    <div css={rootCss}>
      <ProgressBar current={questionPosition} count={questionCount} />
      <QuizElaStandardsSection standards={questionStandard} />
      {questionCount ? (
        <h3 css={questionCountContainerCss} tabIndex={0}>
          Question {`${questionPosition} of ${questionCount}`}
        </h3>
      ) : null}
      <QuizQuestion
        contentId={quizData?.id}
        currentLevelId={levelId}
        language={language}
        isPreviewMode
        question={currentQuestion}
        questionPosition={questionPosition}
        showAnswersInPresentMode={
          !isPresentModeActive ? true : showAnswerInPresentMode
        }
      />
      {isPresentModeActive ? (
        <QuizToggleAnswersButton
          showAnswersInPresentMode={showAnswerInPresentMode}
          questionId={currentQuestion?.id}
          contentId={levelId}
          handleOnClick={() =>
            setShowAnswerInPresentMode(
              (showAnswerInPresentMode) => !showAnswerInPresentMode,
            )
          }
        />
      ) : null}

      {questionCount ? (
        <NavigationButtons>
          <NavigationButtons.QuizNavigationButtons
            currentQuestion={questionPosition}
            handleClick={onClickNavigationButtons}
            objectId={quizId}
            contentId={levelId}
            questionCount={questionCount}
          />
        </NavigationButtons>
      ) : null}
    </div>
  );
};

QuizPreviewContainer.propTypes = propTypes;
export default QuizPreviewContainer;
